import {
  Box,
  Flex,
  HStack,
  Text,
  Heading,
  LinkBox,
  LinkOverlay,
  Spacer,
  useColorModeValue,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  getStatusIcon,
  PipelineStatusIndicator,
} from 'packages/dataPipeline/ui/list/PipelineStatusIndicator';
import {
  getReportingDateVersionLabel,
  getReportingDateVersionShortString,
} from 'packages/dataPipeline/ui/utils/getReportingDateVersionString';
import { getRelativeTime } from 'packages/dataPipeline/ui/utils/getTimeElapsed';
import { SelectedMeshRun } from '~/packages/dataPipeline/types/dataPipelineTypes';

export const MeshCard = ({
  meshRow,
}: {
  meshRow: DataPipeline.MeshRunRecord;
}) => {
  const location = useLocation();
  const searchParams = location.search;

  const reportingDateVersionLabel = getReportingDateVersionLabel(
    meshRow.reportingDate,
    meshRow.fileVersion,
  );
  const startedAtStr = new Date(meshRow.startedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  const titleColor = useColorModeValue(colors.gray[900], colors.gray[100]);
  const dateColor = useColorModeValue(colors.gray[700], colors.gray[200]);
  const subTextColor = useColorModeValue(colors.gray[500], colors.gray[300]);
  const hoverBorderColor = useColorModeValue(
    colors.gray[400],
    colors.gray[500],
  );

  // the whole card opens the run. It is named by its mesh, date and version;
  // its id is only on the details page.
  return (
    <LinkBox
      as="article"
      borderWidth="1px"
      borderRadius="lg"
      px="1.25rem"
      py="1rem"
      transition="border-color 0.15s, box-shadow 0.15s"
      _hover={{ borderColor: hoverBorderColor, boxShadow: 'sm' }}
    >
      <HStack spacing="1rem">
        {/* icon only, so every card's title starts at the same place; above the
            link, so its tooltip shows */}
        <Tooltip label={getStatusIcon(meshRow.status).label} placement="top">
          <Flex
            position="relative"
            zIndex={1}
            flexShrink={0}
            w="2.25rem"
            h="2.25rem"
            alignItems="center"
            justifyContent="center"
          >
            <PipelineStatusIndicator status={meshRow.status} iconOnly />
          </Flex>
        </Tooltip>
        <VStack alignItems="flex-start" spacing="0.375rem" minW={0}>
          <LinkOverlay
            as={Link}
            to={`./details/${meshRow.meshRunId}${searchParams}`}
            aria-label={`${meshRow.meshName}, ${reportingDateVersionLabel}`}
          >
            <Heading
              size="1rem"
              fontWeight={600}
              color={titleColor}
              noOfLines={1}
            >
              {meshRow.meshName}
            </Heading>
          </LinkOverlay>
          <HStack spacing="0.5rem" flexWrap="wrap" fontSize="0.875rem">
            <FaIcon icon="calendar" color={dateColor} />
            <Text fontWeight={500} color={dateColor}>
              {reportingDateVersionLabel}
            </Text>
            <Text color={subTextColor}>·</Text>
            <Text color={subTextColor}>Started {startedAtStr}</Text>
            <Text color={subTextColor}>·</Text>
            <Text color={subTextColor}>
              Updated {getRelativeTime(meshRow.updatedAt)}
            </Text>
          </HStack>
        </VStack>
        <Spacer />
        <FaIcon icon="angle-right" color={subTextColor} size="1rem" />
      </HStack>
    </LinkBox>
  );
};

export const MeshMiniCard = ({
  meshRow,
  onPipelineSelected,
}: {
  meshRow: DataPipeline.MeshRunRecordWithMeshDocument;
  onPipelineSelected(selectedMeshRun: SelectedMeshRun): void;
}) => {
  const reportingDateVersionStr = getReportingDateVersionShortString(
    meshRow.reportingDate,
    meshRow.fileVersion,
  );

  const startedAtStr = new Date(meshRow.startedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  const statusIcon = getStatusIcon(meshRow.status);

  return (
    <Box p="0.875rem" borderWidth="1px" borderRadius="lg" width="100%">
      <HStack
        spacing="1.5rem"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <VStack alignItems="flex-start">
          <Typography fontWeight="medium" trim>
            {meshRow.meshName}
          </Typography>

          <HStack spacing="1rem">
            <Typography variant="detail-muted">
              {reportingDateVersionStr}
            </Typography>
            <Typography variant="detail-muted">
              Started: {startedAtStr}
            </Typography>
          </HStack>
        </VStack>
        <HStack>
          <FaIcon
            icon={statusIcon.icon}
            color={statusIcon.textColor}
            size="1.5rem"
          />
          <Text>{statusIcon.label}</Text>
          <IconButton
            ariaLabel="Open"
            label="Open"
            icon={<FaIcon icon="angle-right" size="1rem" />}
            isDisabled={!meshRow.latestMeshDocument}
            onClick={() => {
              // we allow click only if mesh was run against latest document
              // to prevent selecting stale mesh runs
              if (meshRow.latestMeshDocument) {
                onPipelineSelected({
                  meshRunId: meshRow.meshRunId,
                  meshRunParams: {
                    meshDocumentMetadata: meshRow.latestMeshDocument,
                    rdfv: {
                      reportingDate: meshRow.reportingDate,
                      fileVersion: meshRow.fileVersion,
                    },
                  },
                });
              }
            }}
          />
        </HStack>
      </HStack>
    </Box>
  );
};
